/**
 * Pure form <-> settings mapping for the SAP S/4HANA settings page.
 *
 * Every section card owns a disjoint set of settings keys and contributes a
 * slice via its own mapper. The page saves `{ ...storedSettings, ...slice }`,
 * so a card can only ever write the keys it renders — settings without a
 * dedicated widget survive untouched. Before MEIS-12868 a single mapper wrote
 * every key on every card's save, which is how the business-partner filter
 * came to be overwritten with its own stored value on each save.
 */

// API endpoint pairs grouped for readability
export const API_ENDPOINT_GROUPS = [
  {
    title: "Sales Quotation Services",
    endpoints: [
      ["apiSalesQuotationSrvUrl", "apiSalesQuotationSrvBasePath"],
      ["apiYm1SalesQuotationSrvUrl", "apiYm1SalesQuotationSrvBasePath"],
      ["apiYapiSalesQuotation001SrvUrl", "apiYapiSalesQuotation001SrvBasePath"],
      [
        "apiSalesQuotationYcrSdM1AgCopyUrl",
        "apiSalesQuotationYcrSdM1AgCopyBasePath",
      ],
    ],
  },
  {
    title: "Value Help Services",
    endpoints: [
      ["apiValueHelpCloudReadyV4Url", "apiValueHelpCloudReadyV4BasePath"],
      ["apiValueHelpOnPremiseV4Url", "apiValueHelpOnPremiseV4BasePath"],
    ],
  },
  {
    title: "Product & Inventory Services",
    endpoints: [
      ["apiMdCProductMaintainSrvUrl", "apiMdCProductMaintainSrvBasePath"],
      ["apiMdCustomerMasterSrv01Url", "apiMdCustomerMasterSrv01BasePath"],
      ["apiYmmUiV4KmatMaterialUrl", "apiYmmUiV4KmatMaterialBasePath"],
      ["apiYmmUiV4KmatListungUrl", "apiYmmUiV4KmatListungBasePath"],
      ["apiYmmProductShortTexts04Url", "apiYmmProductShortTexts04BasePath"],
    ],
  },
  {
    title: "Pricing & Conditions",
    endpoints: [
      [
        "apiSlsPricingConditionRecordSrvUrl",
        "apiSlsPricingConditionRecordSrvBasePath",
      ],
      [
        "apiPurgPrcgConditionRecordSrvUrl",
        "apiPurgPrcgConditionRecordSrvBasePath",
      ],
      ["apiYsdSubmitCondCalcSrvUrl", "apiYsdSubmitCondCalcSrvBasePath"],
      ["apiInfoRecordSrvUrl", "apiInfoRecordSrvBasePath"],
    ],
  },
  {
    title: "Business Partners & Documents",
    endpoints: [
      ["apiBusinessPartnerSrvUrl", "apiBusinessPartnerSrvBasePath"],
      ["apiDocumentManagementSrvUrl", "apiDocumentManagementSrvBasePath"],
      ["apiDocumentFlowUrl"],
      ["apiCustomerCreateFisMpmUrl"],
      ["apiSalesQuotationCrmRedirectUrl"],
    ],
  },
];

export const API_ENDPOINT_KEYS: string[] = API_ENDPOINT_GROUPS.flatMap(group =>
  group.endpoints.flat()
);

/** Access levels follow SAP's 1000-based scale; 1000 is the floor. */
export const MIN_PRICING_ACCESS_LEVEL = 1000;

export type SettingsObject = Record<string, unknown>;

// --- card value shapes -----------------------------------------------------

export interface GeneralValues {
  sapPartnerId: string;
  salesQuotationType: string;
  importFromSalesQuotationType: string;
  defaultProductSupplierId: string;
  salesQuotationCreatedByUser: string;
  salesQuotationQuoteIdCustomFieldKey: string;
  hideResetPassword: boolean;
  hideDeleteProfile: boolean;
  disableCraftsmenAccountingFields: boolean;
}

export interface OrganisationValues {
  availableSalesOrganizations: string;
  distributionChannel: string;
  organizationDivision: string;
  fioriBusinessPartnerBasePath: string;
  clientCertificateSecretId: string;
}

export interface QuotesValues {
  salesQuotationStatuses: string;
  salesQuotationInitialStatus: string;
  salesQuotationStatusField: string;
  salesQuotationSdDocumentReasons: string;
  salesQuotationItemSalesDocumentRejectionReasonDefault: string;
  salesQuotationItemSalesDocumentRejectionReasons: string;
  salesQuotationHandleDeletedItemsAsRejected: boolean;
  salesQuotationDummyMaterialNumbers: string;
}

export interface ItemsValues {
  positionStepSize: number | string;
  roundDistributedPositionsTo: number | string;
  firstPosition: number | string;
  minPosition: number | string;
  maxPosition: number | string;
  salesQuotationItemSectionItemCategory: string;
  salesQuotationItemComponentCategory: string;
  salesQuotationItemHeaderCategories: string;
  salesQuotationItemSectionItemBeginItemMaterial: string;
  salesQuotationItemSectionItemEndItemMaterial: string;
  salesQuotationItemPlantDefault: string;
  itemCategoryOverrides: string;
}

export interface ProductsValues {
  configurableProductSupplierCatalogueIds: string;
  kmatListingAssortment: string;
  fisSolrUrl: string;
}

/** One partner function entry of `salesQuotationBusinessPartnerFilterBy`. */
export interface BpFilterRow {
  functionCode: string;
  businessPartnerRolesAll: string;
  businessPartnerRolesExclude: string;
  tagsExclude: string;
}

export interface BusinessPartnersValues {
  bpFilterRows: BpFilterRow[];
  preselectedQuoteBusinessPartnerFunctions: string;
}

/** One role entry of `rolePricingAccessLevel`. */
export interface RolePricingRow {
  role: string;
  accessLevel: number | string;
}

/** One step entry of `pricingProcedureStepAccessLevels`. */
export interface PricingStepRow {
  step: string;
  accessLevel: number | string;
}

export interface PricingVisibilityValues {
  rolePricingRows: RolePricingRow[];
  pricingStepRows: PricingStepRow[];
}

export const CUSTOM_FIELD_ENTITIES = [
  "salesQuotation",
  "salesQuotationItem",
] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

/** One override entry of `customFieldMetadataOverrides`. */
export interface CustomFieldRow {
  entity: CustomFieldEntity;
  fieldKey: string;
  disabled: boolean;
}

export interface CustomFieldsValues {
  customFieldRows: CustomFieldRow[];
}

export interface CrmGdprValues {
  crmUserIdCustomFieldKey: string;
  crmDefaultJobTitle: string;
  crmDefaultCashPlanningGroup: string;
  crmEndClientPaymentTerms: string;
  crmEndClientZugferdMailEnabled: boolean;
  gdprFromEmailAddress: string;
  gdprEmailTemplateId: string;
}

export type ApiEndpointsValues = Record<string, string>;

/** Expert mode: the whole settings object as one JSON text. */
export interface ExpertValues {
  settingsJson: string;
}

// --- helpers ---------------------------------------------------------------

const commaToArray = (value: string): string[] =>
  value
    .split(",")
    .map(entry => entry.trim())
    .filter(Boolean);

const arrayToComma = (value: unknown): string =>
  Array.isArray(value) ? value.map(String).join(", ") : "";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

// --- business partner filter conversion ------------------------------------

export function recordToBpFilterRows(value: unknown): BpFilterRow[] {
  return Object.entries(asRecord(value)).map(([functionCode, entry]) => {
    const filter = asRecord(entry);
    return {
      functionCode,
      businessPartnerRolesAll: arrayToComma(filter.businessPartnerRolesAll),
      businessPartnerRolesExclude: arrayToComma(
        filter.businessPartnerRolesExclude
      ),
      tagsExclude: arrayToComma(filter.tagsExclude),
    };
  });
}

/**
 * Rows -> record. An empty list omits its sub-key rather than storing `[]`,
 * so a stored value round-trips unchanged through the form.
 */
export function bpFilterRowsToRecord(
  rows: BpFilterRow[]
): Record<string, Record<string, string[]>> {
  const record: Record<string, Record<string, string[]>> = {};
  for (const row of rows) {
    const functionCode = row.functionCode.trim();
    if (!functionCode) continue;
    const entry: Record<string, string[]> = {};
    const rolesAll = commaToArray(row.businessPartnerRolesAll);
    const rolesExclude = commaToArray(row.businessPartnerRolesExclude);
    const tagsExclude = commaToArray(row.tagsExclude);
    if (rolesAll.length > 0) entry.businessPartnerRolesAll = rolesAll;
    if (rolesExclude.length > 0) {
      entry.businessPartnerRolesExclude = rolesExclude;
    }
    if (tagsExclude.length > 0) entry.tagsExclude = tagsExclude;
    record[functionCode] = entry;
  }
  return record;
}

export function duplicateBpFunctionCodeIndexes(rows: BpFilterRow[]): number[] {
  const seen = new Map<string, number>();
  const duplicates: number[] = [];
  rows.forEach((row, index) => {
    const code = row.functionCode.trim();
    if (!code) return;
    if (seen.has(code)) duplicates.push(index);
    else seen.set(code, index);
  });
  return duplicates;
}

// --- role pricing access level conversion ----------------------------------

export function recordToRolePricingRows(value: unknown): RolePricingRow[] {
  return Object.entries(asRecord(value)).map(([role, accessLevel]) => ({
    role,
    accessLevel: typeof accessLevel === "number" ? accessLevel : "",
  }));
}

export function rolePricingRowsToRecord(
  rows: RolePricingRow[]
): Record<string, number> {
  const record: Record<string, number> = {};
  for (const row of rows) {
    const role = row.role.trim();
    if (!role) continue;
    record[role] = Number(row.accessLevel);
  }
  return record;
}

export function duplicateRoleIndexes(rows: RolePricingRow[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  rows.forEach((row, index) => {
    const role = row.role.trim();
    if (!role) return;
    if (seen.has(role)) duplicates.push(index);
    else seen.add(role);
  });
  return duplicates;
}

// --- pricing procedure step conversion -------------------------------------

export function listToPricingStepRows(value: unknown): PricingStepRow[] {
  if (!Array.isArray(value)) return [];
  return value.map(entry => {
    const row = asRecord(entry);
    return {
      step: typeof row.step === "string" ? row.step : "",
      accessLevel: typeof row.accessLevel === "number" ? row.accessLevel : "",
    };
  });
}

export function pricingStepRowsToList(
  rows: PricingStepRow[]
): Array<{ step: string; accessLevel: number }> {
  return rows
    .filter(row => row.step.trim() !== "")
    .map(row => ({
      step: row.step.trim(),
      accessLevel: Number(row.accessLevel),
    }));
}

export function duplicateStepIndexes(rows: PricingStepRow[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  rows.forEach((row, index) => {
    const step = row.step.trim();
    if (!step) return;
    if (seen.has(step)) duplicates.push(index);
    else seen.add(step);
  });
  return duplicates;
}

// --- custom field metadata overrides conversion -----------------------------

export function recordToCustomFieldRows(value: unknown): CustomFieldRow[] {
  const record = asRecord(value);
  const rows: CustomFieldRow[] = [];
  for (const entity of CUSTOM_FIELD_ENTITIES) {
    for (const [fieldKey, entry] of Object.entries(asRecord(record[entity]))) {
      rows.push({
        entity,
        fieldKey,
        disabled: asRecord(entry).disabled === true,
      });
    }
  }
  return rows;
}

/**
 * Rows -> nested record. A non-disabled row stores `{}` rather than
 * `{disabled: false}` (the schema flag is optional; absent and false are
 * semantically identical), and an entity without rows omits its sub-key.
 */
export function customFieldRowsToRecord(
  rows: CustomFieldRow[]
): Partial<Record<CustomFieldEntity, Record<string, { disabled?: boolean }>>> {
  const record: Partial<
    Record<CustomFieldEntity, Record<string, { disabled?: boolean }>>
  > = {};
  for (const row of rows) {
    const fieldKey = row.fieldKey.trim();
    if (!fieldKey) continue;
    record[row.entity] ??= {};
    record[row.entity]![fieldKey] = row.disabled ? { disabled: true } : {};
  }
  return record;
}

export function duplicateCustomFieldIndexes(rows: CustomFieldRow[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  rows.forEach((row, index) => {
    const fieldKey = row.fieldKey.trim();
    if (!fieldKey) return;
    const key = `${row.entity}:${fieldKey}`;
    if (seen.has(key)) duplicates.push(index);
    else seen.add(key);
  });
  return duplicates;
}

export function isValidPricingAccessLevel(value: number | string): boolean {
  const level = Number(value);
  return (
    value !== "" && Number.isInteger(level) && level >= MIN_PRICING_ACCESS_LEVEL
  );
}

// --- pricing procedure step validation (mirrors the Zod refinements) -------

/**
 * Mirrors `integrationSettingsSchema.pricingProcedureStepAccessLevels`: a
 * duplicate step would silently override the earlier one and could
 * unintentionally expose or hide sensitive pricing rows, so it is rejected
 * before the mutation rather than only server-side.
 */
export function pricingStepAccessLevelsError(value: unknown): string | null {
  if (!Array.isArray(value)) return "notAnArray";
  const steps = new Set<string>();
  for (const entry of value) {
    const row = asRecord(entry);
    if (typeof row.step !== "string" || row.step === "") return "invalidStep";
    if (
      typeof row.accessLevel !== "number" ||
      !Number.isInteger(row.accessLevel) ||
      row.accessLevel < MIN_PRICING_ACCESS_LEVEL
    ) {
      return "invalidAccessLevel";
    }
    if (steps.has(row.step)) return "duplicateStep";
    steps.add(row.step);
  }
  return null;
}

// --- advanced (JSON) card --------------------------------------------------

/**
 * Settings that have a dedicated widget. Anything else an organisation stores
 * is surfaced by the Advanced (JSON) card, which is what keeps the page from
 * silently hiding a setting again.
 */
export const CARD_OWNED_KEYS: ReadonlySet<string> = new Set<string>([
  // General
  "sapPartnerId",
  "salesQuotationType",
  "importFromSalesQuotationType",
  "defaultProductSupplierId",
  "salesQuotationCreatedByUser",
  "salesQuotationQuoteIdCustomFieldKey",
  "hideResetPassword",
  "hideDeleteProfile",
  "disableCraftsmenAccountingFields",
  // Organisation
  "availableSalesOrganizations",
  "distributionChannel",
  "organizationDivision",
  "fioriBusinessPartnerBasePath",
  "clientCertificateSecretId",
  // Quotes
  "salesQuotationStatuses",
  "salesQuotationInitialStatus",
  "salesQuotationStatusField",
  "salesQuotationSdDocumentReasons",
  "salesQuotationItemSalesDocumentRejectionReasonDefault",
  "salesQuotationItemSalesDocumentRejectionReasons",
  "salesQuotationHandleDeletedItemsAsRejected",
  "salesQuotationDummyMaterialNumbers",
  // Items
  "salesQuotationItemPositionSettings",
  "salesQuotationItemSectionItemCategory",
  "salesQuotationItemComponentCategory",
  "salesQuotationItemHeaderCategories",
  "salesQuotationItemSectionItemBeginItemMaterial",
  "salesQuotationItemSectionItemEndItemMaterial",
  "salesQuotationItemPlantDefault",
  "itemCategoryOverrides",
  // Products
  "configurableProductSupplierCatalogueIds",
  "kmatListingAssortment",
  "fisSolrUrl",
  // Business Partners
  "salesQuotationBusinessPartnerFilterBy",
  "preselectedQuoteBusinessPartnerFunctions",
  // Pricing visibility
  "rolePricingAccessLevel",
  "pricingProcedureStepAccessLevels",
  // Custom Fields
  "customFieldMetadataOverrides",
  // CRM / GDPR
  "crm",
  "gdprEmailConfig",
  // API endpoints
  ...API_ENDPOINT_KEYS,
]);

// --- expert mode ------------------------------------------------------------

export function expertInitialValues(settings: SettingsObject): ExpertValues {
  return { settingsJson: JSON.stringify(settings, null, 2) };
}

/**
 * Parse the expert editor content. The result replaces the stored settings
 * wholesale, so anything but a JSON object is rejected before the mutation.
 */
export function parseExpertSettings(
  text: string
):
  | { ok: true; value: SettingsObject }
  | { ok: false; error: "syntax" | "notAnObject" } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, error: "syntax" };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "notAnObject" };
  }
  return { ok: true, value: parsed as SettingsObject };
}

// --- slice mappers ---------------------------------------------------------

export function toGeneralSlice(values: GeneralValues): SettingsObject {
  return {
    sapPartnerId: values.sapPartnerId,
    salesQuotationType: values.salesQuotationType,
    importFromSalesQuotationType: values.importFromSalesQuotationType,
    defaultProductSupplierId: values.defaultProductSupplierId,
    salesQuotationCreatedByUser: values.salesQuotationCreatedByUser,
    salesQuotationQuoteIdCustomFieldKey:
      values.salesQuotationQuoteIdCustomFieldKey,
    hideResetPassword: values.hideResetPassword,
    hideDeleteProfile: values.hideDeleteProfile,
    disableCraftsmenAccountingFields: values.disableCraftsmenAccountingFields,
  };
}

export function toOrganisationSlice(
  values: OrganisationValues
): SettingsObject {
  return {
    availableSalesOrganizations: commaToArray(
      values.availableSalesOrganizations
    ),
    distributionChannel: values.distributionChannel,
    organizationDivision: values.organizationDivision,
    fioriBusinessPartnerBasePath: values.fioriBusinessPartnerBasePath,
    clientCertificateSecretId: values.clientCertificateSecretId,
  };
}

export function toQuotesSlice(values: QuotesValues): SettingsObject {
  return {
    salesQuotationStatuses: commaToArray(values.salesQuotationStatuses),
    salesQuotationInitialStatus: values.salesQuotationInitialStatus,
    salesQuotationStatusField: values.salesQuotationStatusField,
    salesQuotationSdDocumentReasons: commaToArray(
      values.salesQuotationSdDocumentReasons
    ),
    salesQuotationItemSalesDocumentRejectionReasonDefault:
      values.salesQuotationItemSalesDocumentRejectionReasonDefault,
    salesQuotationItemSalesDocumentRejectionReasons: commaToArray(
      values.salesQuotationItemSalesDocumentRejectionReasons
    ),
    salesQuotationHandleDeletedItemsAsRejected:
      values.salesQuotationHandleDeletedItemsAsRejected,
    salesQuotationDummyMaterialNumbers: commaToArray(
      values.salesQuotationDummyMaterialNumbers
    ),
  };
}

export function toItemsSlice(values: ItemsValues): SettingsObject {
  return {
    salesQuotationItemPositionSettings: {
      positionStepSize: Number(values.positionStepSize),
      roundDistributedPositionsTo: Number(values.roundDistributedPositionsTo),
      firstPosition: Number(values.firstPosition),
      minPosition: Number(values.minPosition),
      maxPosition: values.maxPosition ? Number(values.maxPosition) : null,
    },
    salesQuotationItemSectionItemCategory:
      values.salesQuotationItemSectionItemCategory,
    salesQuotationItemComponentCategory:
      values.salesQuotationItemComponentCategory,
    salesQuotationItemHeaderCategories: commaToArray(
      values.salesQuotationItemHeaderCategories
    ),
    salesQuotationItemSectionItemBeginItemMaterial:
      values.salesQuotationItemSectionItemBeginItemMaterial,
    salesQuotationItemSectionItemEndItemMaterial:
      values.salesQuotationItemSectionItemEndItemMaterial,
    salesQuotationItemPlantDefault: values.salesQuotationItemPlantDefault,
    itemCategoryOverrides: commaToArray(values.itemCategoryOverrides),
  };
}

export function toProductsSlice(values: ProductsValues): SettingsObject {
  return {
    configurableProductSupplierCatalogueIds: commaToArray(
      values.configurableProductSupplierCatalogueIds
    ),
    kmatListingAssortment: values.kmatListingAssortment,
    // an emptied optional URL removes the key
    fisSolrUrl: values.fisSolrUrl || undefined,
  };
}

export function toBusinessPartnersSlice(
  values: BusinessPartnersValues
): SettingsObject {
  return {
    salesQuotationBusinessPartnerFilterBy: bpFilterRowsToRecord(
      values.bpFilterRows
    ),
    preselectedQuoteBusinessPartnerFunctions: commaToArray(
      values.preselectedQuoteBusinessPartnerFunctions
    ),
  };
}

export function toPricingVisibilitySlice(
  values: PricingVisibilityValues
): SettingsObject {
  return {
    rolePricingAccessLevel: rolePricingRowsToRecord(values.rolePricingRows),
    pricingProcedureStepAccessLevels: pricingStepRowsToList(
      values.pricingStepRows
    ),
  };
}

export function toCustomFieldsSlice(
  values: CustomFieldsValues
): SettingsObject {
  return {
    customFieldMetadataOverrides: customFieldRowsToRecord(
      values.customFieldRows
    ),
  };
}

export function toCrmGdprSlice(
  values: CrmGdprValues,
  existingSettings: SettingsObject = {}
): SettingsObject {
  return {
    // `crm` may also hold settings this card does not render a widget for
    // yet — start from the stored object so saving this card cannot
    // silently delete them, then overwrite every key the card does render
    // (including endClientPaymentTerms / endClientZugferdMailEnabled) with
    // the submitted form values.
    crm: {
      ...asRecord(existingSettings.crm),
      userIdcustomFieldKey: values.crmUserIdCustomFieldKey,
      defaultJobTitle: values.crmDefaultJobTitle || undefined,
      defaultCashPlanningGroup: values.crmDefaultCashPlanningGroup || undefined,
      // an emptied field lets the schema's "VK" default apply
      endClientPaymentTerms: values.crmEndClientPaymentTerms || undefined,
      // absent and `false` are semantically identical, so an off switch
      // does not materialise the key
      endClientZugferdMailEnabled: values.crmEndClientZugferdMailEnabled
        ? true
        : undefined,
    },
    gdprEmailConfig: {
      fromEmailAddess: values.gdprFromEmailAddress,
      emailTemplateId: values.gdprEmailTemplateId,
    },
  };
}

export function toApiEndpointsSlice(
  values: ApiEndpointsValues
): SettingsObject {
  const slice: SettingsObject = {};
  for (const key of API_ENDPOINT_KEYS) {
    slice[key] = values[key] ?? "";
  }
  return slice;
}

// --- initial values -------------------------------------------------------

export function generalInitialValues(settings: SettingsObject): GeneralValues {
  return {
    sapPartnerId: (settings.sapPartnerId as string) ?? "",
    salesQuotationType: (settings.salesQuotationType as string) ?? "",
    importFromSalesQuotationType:
      (settings.importFromSalesQuotationType as string) ?? "",
    defaultProductSupplierId:
      (settings.defaultProductSupplierId as string) ?? "",
    salesQuotationCreatedByUser:
      (settings.salesQuotationCreatedByUser as string) ?? "",
    salesQuotationQuoteIdCustomFieldKey:
      (settings.salesQuotationQuoteIdCustomFieldKey as string) ?? "",
    hideResetPassword: (settings.hideResetPassword as boolean) ?? false,
    hideDeleteProfile: (settings.hideDeleteProfile as boolean) ?? false,
    disableCraftsmenAccountingFields:
      (settings.disableCraftsmenAccountingFields as boolean) ?? false,
  };
}

export function organisationInitialValues(
  settings: SettingsObject
): OrganisationValues {
  return {
    availableSalesOrganizations: arrayToComma(
      settings.availableSalesOrganizations
    ),
    distributionChannel: (settings.distributionChannel as string) ?? "",
    organizationDivision: (settings.organizationDivision as string) ?? "",
    fioriBusinessPartnerBasePath:
      (settings.fioriBusinessPartnerBasePath as string) ?? "",
    clientCertificateSecretId:
      (settings.clientCertificateSecretId as string) ?? "",
  };
}

export function quotesInitialValues(settings: SettingsObject): QuotesValues {
  return {
    salesQuotationStatuses: arrayToComma(settings.salesQuotationStatuses),
    salesQuotationInitialStatus:
      (settings.salesQuotationInitialStatus as string) ?? "",
    salesQuotationStatusField:
      (settings.salesQuotationStatusField as string) ?? "",
    salesQuotationSdDocumentReasons: arrayToComma(
      settings.salesQuotationSdDocumentReasons
    ),
    salesQuotationItemSalesDocumentRejectionReasonDefault:
      (settings.salesQuotationItemSalesDocumentRejectionReasonDefault as string) ??
      "",
    salesQuotationItemSalesDocumentRejectionReasons: arrayToComma(
      settings.salesQuotationItemSalesDocumentRejectionReasons
    ),
    salesQuotationHandleDeletedItemsAsRejected:
      (settings.salesQuotationHandleDeletedItemsAsRejected as boolean) ?? false,
    salesQuotationDummyMaterialNumbers: arrayToComma(
      settings.salesQuotationDummyMaterialNumbers
    ),
  };
}

export function itemsInitialValues(settings: SettingsObject): ItemsValues {
  const position = asRecord(settings.salesQuotationItemPositionSettings);
  return {
    positionStepSize: (position.positionStepSize as number) ?? 10,
    roundDistributedPositionsTo:
      (position.roundDistributedPositionsTo as number) ?? 10,
    firstPosition: (position.firstPosition as number) ?? 1,
    minPosition: (position.minPosition as number) ?? 1,
    maxPosition: (position.maxPosition as number) ?? "",
    salesQuotationItemSectionItemCategory:
      (settings.salesQuotationItemSectionItemCategory as string) ?? "",
    salesQuotationItemComponentCategory:
      (settings.salesQuotationItemComponentCategory as string) ?? "",
    salesQuotationItemHeaderCategories: arrayToComma(
      settings.salesQuotationItemHeaderCategories
    ),
    salesQuotationItemSectionItemBeginItemMaterial:
      (settings.salesQuotationItemSectionItemBeginItemMaterial as string) ?? "",
    salesQuotationItemSectionItemEndItemMaterial:
      (settings.salesQuotationItemSectionItemEndItemMaterial as string) ?? "",
    salesQuotationItemPlantDefault:
      (settings.salesQuotationItemPlantDefault as string) ?? "",
    itemCategoryOverrides: arrayToComma(settings.itemCategoryOverrides),
  };
}

export function productsInitialValues(
  settings: SettingsObject
): ProductsValues {
  return {
    configurableProductSupplierCatalogueIds: arrayToComma(
      settings.configurableProductSupplierCatalogueIds
    ),
    kmatListingAssortment: (settings.kmatListingAssortment as string) ?? "",
    fisSolrUrl: (settings.fisSolrUrl as string) ?? "",
  };
}

export function businessPartnersInitialValues(
  settings: SettingsObject
): BusinessPartnersValues {
  return {
    bpFilterRows: recordToBpFilterRows(
      settings.salesQuotationBusinessPartnerFilterBy
    ),
    preselectedQuoteBusinessPartnerFunctions: arrayToComma(
      settings.preselectedQuoteBusinessPartnerFunctions
    ),
  };
}

export function pricingVisibilityInitialValues(
  settings: SettingsObject
): PricingVisibilityValues {
  return {
    rolePricingRows: recordToRolePricingRows(settings.rolePricingAccessLevel),
    pricingStepRows: listToPricingStepRows(
      settings.pricingProcedureStepAccessLevels
    ),
  };
}

export function customFieldsInitialValues(
  settings: SettingsObject
): CustomFieldsValues {
  return {
    customFieldRows: recordToCustomFieldRows(
      settings.customFieldMetadataOverrides
    ),
  };
}

export function crmGdprInitialValues(settings: SettingsObject): CrmGdprValues {
  const crm = asRecord(settings.crm);
  const gdpr = asRecord(settings.gdprEmailConfig);
  return {
    crmUserIdCustomFieldKey: (crm.userIdcustomFieldKey as string) ?? "",
    crmDefaultJobTitle: (crm.defaultJobTitle as string) ?? "",
    crmDefaultCashPlanningGroup: (crm.defaultCashPlanningGroup as string) ?? "",
    crmEndClientPaymentTerms: (crm.endClientPaymentTerms as string) ?? "",
    crmEndClientZugferdMailEnabled:
      (crm.endClientZugferdMailEnabled as boolean) ?? false,
    gdprFromEmailAddress: (gdpr.fromEmailAddess as string) ?? "",
    gdprEmailTemplateId: (gdpr.emailTemplateId as string) ?? "",
  };
}

export function apiEndpointsInitialValues(
  settings: SettingsObject
): ApiEndpointsValues {
  const values: ApiEndpointsValues = {};
  for (const key of API_ENDPOINT_KEYS) {
    values[key] = (settings[key] as string) ?? "";
  }
  return values;
}
